import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowRight, Sparkles, FileText, UserCog } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import Logo from "@/components/Logo";
import Markdown from "@/components/Markdown";
import { Skeleton } from "@/components/Skeleton";
import OGTags from "@/components/OGTags";

export default function PublicResume() {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [resume, setResume] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/resume/${shareToken}`);
        setResume(data);
      } catch { setNotFound(true); }
    })();
  }, [shareToken]);

  if (notFound) {
    return (
      <Shell>
        <div className="tb-card p-10 text-center max-w-md mx-auto">
          <span className="mx-auto h-14 w-14 grid place-items-center rounded-full border border-dashed border-border mb-3">
            <FileText className="h-6 w-6 text-muted-foreground" />
          </span>
          <p className="font-display text-lg font-medium">Resume not found</p>
          <p className="mt-1 text-sm text-muted-foreground">The author may have unpublished it.</p>
          <Button className="mt-4 rounded-full" onClick={() => navigate("/")}>Go to TeeBee</Button>
        </div>
      </Shell>
    );
  }

  if (!resume) {
    return <Shell><Skeleton className="h-96 max-w-3xl mx-auto" /></Shell>;
  }

  return (
    <Shell>
      <OGTags
        title={resume.author_name ? `${resume.author_name} — TeeBee Resume` : "TeeBee Resume"}
        description={`Resume of ${resume.author_name || "an educator"} on TeeBee · by Factskoot.`}
        image={`${window.location.origin}/api/og/resume/${shareToken}`}
        type="profile"
      />
      <div className="mb-4 print:hidden flex flex-wrap gap-2 justify-end">
        {resume.allow_download && (
          <Button variant="outline" className="rounded-full" onClick={() => window.print()} data-testid="public-resume-print">
            <Printer className="h-3.5 w-3.5 mr-1.5" /> Print / PDF
          </Button>
        )}
        {!user && (
          <Button className="rounded-full" onClick={() => navigate("/signup")} data-testid="public-resume-signup">
            Build yours <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        )}
      </div>

      <article
        className={`tb-card p-8 sm:p-12 print:p-0 print:shadow-none print:border-0 tb-resume-${resume.template} max-w-4xl mx-auto`}
        data-testid="public-resume-content"
        id="resume-print-area"
      >
        <Markdown>{resume.content}</Markdown>
      </article>

      <div className="mt-6 max-w-3xl mx-auto tb-card-feature text-center bg-gradient-to-br from-primary/5 via-card to-primary/5 print:hidden">
        <div className="mx-auto h-12 w-12 rounded-full tb-ai-orb grid place-items-center mb-3 tb-float">
          <Sparkles className="h-5 w-5 text-white" />
        </div>
        <h3 className="font-display text-xl tracking-tight font-medium">Build your own AI educator resume</h3>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-md mx-auto">
          TeeBee generates premium resumes, CVs and portfolios from your teaching profile in seconds.
        </p>
        <div className="mt-4 flex flex-wrap gap-2 justify-center">
          {user ? (
            <Button className="rounded-full" onClick={() => navigate("/resume")}>
              <FileText className="h-4 w-4 mr-2" /> Open Resume Builder
            </Button>
          ) : (
            <>
              <Button className="rounded-full" onClick={() => navigate("/signup")}>Sign up free</Button>
              <Button variant="outline" className="rounded-full" onClick={() => navigate("/login")}>I have an account</Button>
            </>
          )}
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  return (
    <div className="min-h-screen tb-auth-bg print:bg-white">
      <header className="sticky top-0 z-30 tb-glass border-b border-border print:hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="public-resume-logo"><Logo size="sm" to={null} /></button>
          <div className="flex items-center gap-2">
            {user ? (
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => navigate("/dashboard")}>Dashboard</Button>
            ) : (
              <Button size="sm" className="rounded-full" onClick={() => navigate("/signup")}>Try TeeBee free</Button>
            )}
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12 print:p-0 print:max-w-none">{children}</main>
    </div>
  );
}
